if (!customElements.get('variant-swatcher')) {
  customElements.define(
    'variant-swatcher',
    class VariantSwatcher extends HTMLElement {
      constructor() {
        super();

        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleOptionInput = this.handleOptionInput.bind(this);
        this.handlePossibleRerender = this.handlePossibleRerender.bind(this);

        this.productForm = null;
        this.variantSelects = null;
        this.variantMap = null;

        this.productInfoObserver = null;
        this.isAvailabilityUpdateQueued = false;
        this.isRerenderCheckQueued = false;

        this.currentTierValue = null;
        this.tierSwapAbort = null;
      }

      get useDynamicTier() {
        return this.hasAttribute('use-dynamic-tier');
      }

      connectedCallback() {
        this.productForm = this.resolveProductForm();
        if (this.productForm) {
          this.productForm.addEventListener('variant:change', this.handleVariantChange);
        }

        this.variantSelects = this.resolveVariantSelects();
        this.attachVariantSelectsListeners();

        this.observeProductInfoRerenders();

        if (this.useDynamicTier) {
          this.currentTierValue = this.getSelectedTierValue();
        }

        const initialVariantId = this.getInitialVariantId();

        if (initialVariantId && this.useDynamicTier) {
          // run once on load (use microtask so DOM is stable)
          queueMicrotask(() => {
            const initialVariantId = this.getInitialVariantId();
            const initialView = this.getSelectedTierViewName();

            // Only run if tier provides a view name
            if (initialVariantId && initialView) {
              this.fetchAndSwapDynamicWrapperByVariantId(initialVariantId, initialView)
                .catch((err) => {
                  if (err?.name !== 'AbortError') console.error(err);
                });
            }
          });
        }

        this.applyVariantMediaById(initialVariantId);

        this.scheduleAvailabilityUpdate();

        if (this.isOptionsPriceEnabled()) {
          queueMicrotask(() => this.updateTierLabelPrices());
        }
      }

      disconnectedCallback() {
        if (this.productForm) {
          this.productForm.removeEventListener('variant:change', this.handleVariantChange);
        }

        this.detachVariantSelectsListeners();

        this.productInfoObserver?.disconnect?.();
        this.productInfoObserver = null;

        this.productForm = null;
        this.variantSelects = null;
        this.variantMap = null;
      }

      handleOptionInput() {
        this.scheduleAvailabilityUpdate();
        if (this.isOptionsPriceEnabled()) this.updateTierLabelPrices();
      }

      updateComparisonImages(variantId) {
        if (!variantId) return;

        const mapScript = document.querySelector('[data-variant-map]');
        if (!mapScript) return;

        let variantMap;
        try {
          variantMap = JSON.parse(mapScript.textContent);
        } catch(e) {
          return;
        }

        const variantData = variantMap.find(v => String(v.id) === String(variantId));
        if (!variantData) return;

        const beforeImg = document.querySelector('.comparison__img--before');
        const afterImg = document.querySelector('.comparison__img--after');
        const beforeSource = document.querySelector('.comparison__source--before');
        const afterSource  = document.querySelector('.comparison__source--after');

        if (!beforeImg || !afterImg) return;

        const hasBefore = !!variantData.comparison_before_image;
        const hasAfter = !!variantData.comparison_after_image;

        // before
        if (hasBefore) {
          beforeImg.src = variantData.comparison_before_image;
          beforeImg.srcset = variantData.comparison_before_image;
        } else if (beforeImg.dataset.defaultSrc) {
          beforeImg.src = beforeImg.dataset.defaultSrc;
          beforeImg.srcset = beforeImg.dataset.defaultSrc;
        }

        // after
        if (hasAfter) {
          afterImg.src = variantData.comparison_after_image;
          afterImg.srcset = variantData.comparison_after_image;
        } else if (afterImg.dataset.defaultSrc) {
          afterImg.src = afterImg.dataset.defaultSrc;
          afterImg.removeAttribute('srcset');
        }
      }

      getVariantDetailsSection(){
        return this._variantDetailsSection = this._variantDetailsSection || document.querySelector('[variant-details-section]')
      }

      renderRichHtml(el, maybeEscapedHtml) {
        const raw = String(maybeEscapedHtml || "");

        const decode = (s) => {
          const t = document.createElement('textarea');
          t.innerHTML = s;
          return t.value;
        };

        const html = raw.includes('&lt;') ? decode(raw) : raw;

        const tpl = document.createElement('template');
        tpl.innerHTML = html;
        el.replaceChildren(tpl.content.cloneNode(true));
      }

      handleVariantDetailsChange(variantId){
        const section = this.getVariantDetailsSection();
        if (!section) return;

        const variant = this.getVariantMap().find((variantEntry) => String(variantEntry.id) === String(variantId));
        if (!variant) return;

        const details_rich_text_map = ["description"]
        details_rich_text_map.forEach((detail) => {
          const el = section.querySelector(`[data-variant-detail="${detail}"]`);
          if (!el) return;

          el.closest('.accordion').hidden = !variant?.[detail];

          const html = variant?.[detail] || "";

          requestAnimationFrame(() => {
            this.renderRichHtml(el, html);
          });
        });

        const details_text_map = ["description","frame-shape", "frame-finish", "lens-tech", "face-size", "frame-height", "frame-width", "lens-width", "nose-bridge", "temple-length"]
        details_text_map.forEach((detail) => {
          const el = section.querySelector(`[data-variant-detail="${detail}"]`);
          if (el) {
            const newValue = variant?.[detail] || '';
            el.textContent = newValue;
          }
        })
      }

      handleVariantChange(event) {
        const selectedVariant = event?.detail?.variant;
        if (!selectedVariant) return;

        if (this.useDynamicTier){
          const nextTierValue = this.getSelectedTierValue();
          const prevTierValue = this.currentTierValue;

          // Update stored value early (so rapid changes don't double-trigger weirdly)
          this.currentTierValue = nextTierValue;

          // Only do heavy swap when tier actually changed
          const tierChanged = nextTierValue && prevTierValue && String(nextTierValue) !== String(prevTierValue);

          if (tierChanged) {
            const view = this.getSelectedTierViewName();
            // Fetch view + swap all sections except first
            if (view) {
              this.fetchAndSwapDynamicWrapperByVariantId(selectedVariant.id, view).catch((err) => {
                if (err?.name !== 'AbortError') console.error(err);
              });
            }
          }
        }

        const mediaGallery = this.getMediaGallery();
        if (mediaGallery) {
          const imagesRaw = this.getVariantImagesById(selectedVariant.id);
          const videosRaw = this.getVariantVideosById(selectedVariant.id);
          if (imagesRaw.length || videosRaw.length) {
            const images = imagesRaw
              .map((item) => {
                if (typeof item === 'string') return { src: item, alt: '' };
                return { src: item?.src, alt: item?.alt || '' };
              })
              .filter((x) => x?.src);

            const videos = videosRaw
              .map((item) => {
                if (typeof item === 'string') return { src: item, alt: '' };
                return { src: item?.src, alt: item?.alt || '', poster: item?.poster || '' };
              })
              .filter((x) => x?.src);

            this.rebuildGallery(mediaGallery, images, videos);
          }
        }

        this.handleVariantDetailsChange(selectedVariant.id)
        
        if (this.useDynamicTier) {
          this.updateComparisonImages(selectedVariant.id);
        }

        this.updateRxEquivalent(selectedVariant.id);

        this.updateFsTierContent(selectedVariant.id);

        this.updateSpecListSections(selectedVariant.id);

        this.scheduleAvailabilityUpdate();
        if (this.isOptionsPriceEnabled()) this.updateTierLabelPrices();

      }

      handlePossibleRerender() {
        if (this.isRerenderCheckQueued) return;
        this.isRerenderCheckQueued = true;

        queueMicrotask(() => {
          this.isRerenderCheckQueued = false;

          const nextVariantSelects = this.resolveVariantSelects();
          if (!nextVariantSelects || nextVariantSelects === this.variantSelects) return;

          this.detachVariantSelectsListeners();
          this.variantSelects = nextVariantSelects;
          this.attachVariantSelectsListeners();

          this.variantMap = null;

          this.scheduleAvailabilityUpdate();
          if (this.isOptionsPriceEnabled()) this.updateTierLabelPrices();
        });
      }

      attachVariantSelectsListeners() {
        if (!this.variantSelects) return;
        this.variantSelects.addEventListener('change', this.handleOptionInput);
      }

      detachVariantSelectsListeners() {
        if (!this.variantSelects) return;
        this.variantSelects.removeEventListener('change', this.handleOptionInput);
      }

      observeProductInfoRerenders() {
        if (this.productInfoObserver) return;

        const sectionRoot =
          this.closest('.shopify-section') ||
          this.closest('[id^="shopify-section-"]') ||
          this.parentElement;

        if (!sectionRoot) return;

        this.productInfoObserver = new MutationObserver((mutations) => {
          if (!mutations || !mutations.length) return;

          for (const m of mutations) {
            if (m.type !== 'childList') continue;

            for (const node of m.addedNodes) {
              if (node?.nodeType !== 1) continue;
              if (node.matches?.('variant-selects') || node.querySelector?.('variant-selects')) {
                this.handlePossibleRerender();
                return;
              }
            }

            for (const node of m.removedNodes) {
              if (node?.nodeType !== 1) continue;
              if (node.matches?.('variant-selects') || node.querySelector?.('variant-selects')) {
                this.handlePossibleRerender();
                return;
              }
            }
          }
        });

        this.productInfoObserver.observe(sectionRoot, { childList: true, subtree: true });
      }


      scheduleAvailabilityUpdate() {
        if (this.isAvailabilityUpdateQueued) return;
        this.isAvailabilityUpdateQueued = true;

        queueMicrotask(() => {
          this.isAvailabilityUpdateQueued = false;
          this.applyAvailabilityUI();
        });
      }

      resolveProductForm() {
        const explicitFormId = this.getAttribute('form');
        if (explicitFormId && document.forms[explicitFormId]) return document.forms[explicitFormId];

        const localVariantSelects = this.querySelector('variant-selects');
        const variantSelectsFormId = localVariantSelects?.getAttribute('form');
        if (variantSelectsFormId && document.forms[variantSelectsFormId]) return document.forms[variantSelectsFormId];

        return this.closest('form');
      }

      resolveVariantSelects() {
        const form = this.productForm || this.resolveProductForm();
        if (form) {
          const byForm = form.querySelector('variant-selects');
          if (byForm) return byForm;
        }
        return this.querySelector('variant-selects') || this.closest('.shopify-section')?.querySelector('variant-selects');
      }

      getMediaGallery() {
        return (
          this.closest('section')?.querySelector('media-gallery[data-media-gallery]') ||
          this.closest('.shopify-section')?.querySelector('media-gallery')
        );
      }

      getOptionGroups() {
        if (!this.variantSelects) return [];
        return Array.from(this.variantSelects.querySelectorAll('[data-option-index]'));
      }

      getSelectedOptionValues(optionGroups) {
        if (!optionGroups?.length) return [];

        return optionGroups.map((optionGroup) => {
          const checkedRadio = optionGroup.querySelector('input[type="radio"]:checked');
          if (checkedRadio) return checkedRadio.value;

          return null;
        });
      }

      getVariantMap() {
        if (this.variantMap) return this.variantMap;

        const variantMapScript =
          this.querySelector('[data-variant-map]') ||
          this.closest('.shopify-section')?.querySelector('[data-variant-map]');

        if (!variantMapScript?.textContent) return (this.variantMap = []);

        try {
          return (this.variantMap = JSON.parse(variantMapScript.textContent || '[]'));
        } catch {
          return (this.variantMap = []);
        }
      }

      getVariantImagesById(variantId) {
        if (!variantId) return [];

        const variantEntry = this.getVariantMap().find((variantEntry) => String(variantEntry.id) === String(variantId));
        const images = Array.isArray(variantEntry?.images) ? variantEntry.images : [];
        return images.filter((img) => (typeof img === 'string' ? img : img?.src));
      }

      getVariantVideosById(variantId) {
        if (!variantId) return [];

        const variantEntry = this.getVariantMap().find((variantEntry) => String(variantEntry.id) === String(variantId));
        const videos = Array.isArray(variantEntry?.videos) ? variantEntry.videos : [];
        return videos.filter((video) => (typeof video === 'string' ? video : video?.src));
      }

      computeExistingAndAvailableValues(selectedOptionValues, optionGroups, targetOptionIndex) {
        const variants = this.getVariantMap();
        const optionCount = optionGroups.length;
        const existingValues = new Set();
        const availableValues = new Set();

        variants.forEach((variantEntry) => {
          if (!variantEntry || !Array.isArray(variantEntry.options)) return;

          for (let optionIndex = 0; optionIndex < optionCount; optionIndex++) {
            if (optionIndex === targetOptionIndex) continue;
            const selectedValue = selectedOptionValues[optionIndex];
            if (selectedValue && variantEntry.options[optionIndex] !== selectedValue) return;
          }

          const value = variantEntry.options[targetOptionIndex];
          existingValues.add(value);
          if (variantEntry.available) availableValues.add(value);
        });

        return { existingValues, availableValues };
      }


      applyAvailabilityUI() {
        if (!this.variantSelects) return;

        const variantMap = this.getVariantMap();
        if (!variantMap.length) return;

        const optionGroups = this.getOptionGroups();
        if (!optionGroups.length) return;

        const colorGroupIndex = this.getColorGroupIndex(optionGroups);
        if (colorGroupIndex === -1) return;

        const selectedOptionValues = this.getSelectedOptionValues(optionGroups);

        const { existingValues, availableValues } = this.computeExistingAndAvailableValues(
          selectedOptionValues,
          optionGroups,
          colorGroupIndex
        );

        const colorGroup = optionGroups[colorGroupIndex];

        const tierGroup = optionGroups.find((g) => g.getAttribute('data-option-slug') === 'tier');
        if (tierGroup) {
          const tierValues = Array.from(tierGroup.querySelectorAll('input[type="radio"]'))
            .map((r) => r.closest('li') || r.parentElement)
            .filter((el) => el && !el.hidden);

          tierGroup.style.display = tierValues.length <= 1 ? 'none' : '';
        }

        const colorRadios = Array.from(colorGroup.querySelectorAll('input[type="radio"]'));
        for (const radio of colorRadios) {
          const optionItem = radio.closest('li') || radio.parentElement;

          const exists = existingValues.has(radio.value);
          const isAvailable = availableValues.has(radio.value);

          const hasVisibleContent = !!optionItem?.querySelector('label img');

          if (optionItem) {
            const shouldHide = !exists;
            optionItem.hidden = shouldHide;
            optionItem.classList.toggle('hidden', shouldHide);
            if (!isAvailable && !hasVisibleContent) {
              optionItem.setAttribute('aria-hidden', 'true');
            } else {
              optionItem.removeAttribute('aria-hidden');
            }
          }

          const shouldLookDisabled = exists && !isAvailable;
          radio.classList.toggle('disabled', shouldLookDisabled);

          radio.disabled = !isAvailable && !hasVisibleContent;
        }

        const seasonalGroup = colorGroup.querySelector('.color-group--seasonal');
        if (seasonalGroup) {
          const visibleItems = seasonalGroup.querySelectorAll('li:not(.hidden):not([hidden])');
          seasonalGroup.hidden = visibleItems.length === 0;
        }

        const regularGroup = colorGroup.querySelector('.color-group--regular');
        if (regularGroup) {
          const visibleItems = regularGroup.querySelectorAll('li:not(.hidden):not([hidden])');
          regularGroup.hidden = visibleItems.length === 0;
        }

                const checkedRadio = colorGroup.querySelector('input[type="radio"]:checked');
                const selectedItem = checkedRadio?.closest('li');
        const isSeasonalSelected = selectedItem?.dataset.seasonalColor === 'true';

        const mainSelectedLabel = colorGroup
          .closest('variant-selects')
          ?.querySelector('[data-option-selected-value]');

        const seasonalSelectedLabel = colorGroup.querySelector('[data-seasonal-selected-value]');

        if (mainSelectedLabel) {
          mainSelectedLabel.textContent = isSeasonalSelected
            ? ''
            : (checkedRadio?.value || '');
        }

        if (seasonalSelectedLabel) {
          seasonalSelectedLabel.textContent = isSeasonalSelected
            ? (checkedRadio?.value || '')
            : '';
        }
        const selectedValue = checkedRadio?.value;

        if (!selectedValue || !existingValues.has(selectedValue) || (checkedRadio?.disabled && availableValues.size > 0)) {
          this.ensureColorSelectionIsAvailable(colorGroup, existingValues, availableValues);
        }
      }

      getInitialVariantId() {
        const selectedVariantScript =
          this.querySelector('variant-selects [data-selected-variant]') ||
          this.querySelector('[data-selected-variant]') ||
          this.closest('.shopify-section')?.querySelector('variant-selects [data-selected-variant]') ||
          this.closest('.shopify-section')?.querySelector('[data-selected-variant]');

        if (selectedVariantScript?.textContent) {
          try {
            const variant = JSON.parse(selectedVariantScript.textContent);
            if (variant?.id) return String(variant.id);
          } catch {}
        }

        const form = this.productForm || this.resolveProductForm();
        const variantIdInput = form?.querySelector('input[name="id"]');
        if (variantIdInput?.value) return String(variantIdInput.value);

        return null;
      }

      applyVariantMediaById(variantId) {
        if (!variantId) return;

        const mediaGallery = this.getMediaGallery();
        if (!mediaGallery) return;

        const imagesRaw = this.getVariantImagesById(variantId);
        const videosRaw = this.getVariantVideosById(variantId);
        if (imagesRaw.length || videosRaw.length) {
          const images = imagesRaw
            .map((item) => {
              if (typeof item === 'string') return { src: item, alt: '' };
              return { src: item?.src, alt: item?.alt || '' };
            })
            .filter((x) => x?.src);

          const videos = videosRaw
            .map((item) => {
              if (typeof item === 'string') return { src: item, alt: '' };
              return { src: item?.src, alt: item?.alt || '', poster: item?.poster || '' };
            })
            .filter((x) => x?.src);

          this.rebuildGallery(mediaGallery, images, videos);
        }
      }

      rebuildGallery(mediaGallery, images, videos) {
        const sliderElement = mediaGallery.querySelector('slider-element');
        const mediaList = sliderElement?.querySelector('.product__media-list');
        const mediaDots = mediaGallery.querySelector('media-dots');

        if (!sliderElement || !mediaList) return;

        const slideImageTemplate = (
          mediaList.querySelector('.product__media[data-media-type="image"]') || mediaList.querySelector('.product__media')
        )?.cloneNode(true);

        const slideVideoTemplate = (
          mediaList.querySelector('.product__media[data-media-type="video"]')
        )?.cloneNode(true);

        const dotTemplate = (mediaDots?.querySelector('button[data-media-id]') || mediaDots?.querySelector('button'))?.cloneNode(
          true
        );

        if (!slideImageTemplate && !slideVideoTemplate) return;

        const lockScroll = (el) => {
          const x = el.scrollLeft;

          const preventScroll = () => {
            if (el.scrollLeft !== x) {
              el.scrollLeft = x;
            }
          };

          el.addEventListener('scroll', preventScroll);

          return () => {
            el.removeEventListener('scroll', preventScroll);
          };
        };

        const unlockScroll = lockScroll(sliderElement);
        sliderElement.style.overflow = 'hidden';
        mediaList.style.overflow = 'hidden';

        mediaList.innerHTML = '';
        if (mediaDots) mediaDots.innerHTML = '';
        sliderElement.scrollLeft = 0;

        const slidesFragment = document.createDocumentFragment();
        const allMedia = [
          ...images.map(img => ({ ...img, type: 'image' })),
          ...videos.map(vid => ({ ...vid, type: 'video' }))
        ];

        allMedia.forEach((mediaData, index) => {
          const isVideo = mediaData.type === 'video';
          const template = isVideo ? (slideVideoTemplate || slideImageTemplate) : slideImageTemplate;
          const slide = template.cloneNode(true);
          const uniqueId = `custom-${index + 1}`;

          slide.setAttribute('data-media-type', mediaData.type);
          slide.setAttribute('data-media-id', uniqueId);
          slide.removeAttribute('hidden');
          slide.classList.remove('xl:hidden', 'loading');

          // only show on first slide, remove from others (to avoid duplicate content)
          if (index > 0) {
            slide.querySelector('.inventory-callout')?.remove();
            slide.querySelector('.badges')?.remove();
            slide.querySelector('.one-time-image')?.remove();
          }

          if (isVideo) {
            slide.setAttribute('data-prevent-autoplay', '');
            const videoMedia = slide.tagName === 'VIDEO-MEDIA' ? slide : slide.querySelector('video-media');

            if (videoMedia) {
              videoMedia.setAttribute('aria-controls', `Video-${uniqueId}`);
            }

            const poster = slide.querySelector('[id^="DeferredPoster-"]');
            const deferredTemplate = slide.querySelector('template');
            if (poster) poster.id = `DeferredPoster-${uniqueId}`;

            const searchArea = deferredTemplate ? deferredTemplate.content : slide;
            const videoElement = searchArea.querySelector('video');
            const sourceElement = searchArea.querySelector('source');

            if (videoElement) {
              videoElement.id = `Video-${uniqueId}`;
              videoElement.setAttribute('muted', '');
              videoElement.setAttribute('preload', 'metadata');

              if (sourceElement) sourceElement.src = mediaData.src;
              else videoElement.src = mediaData.src;

              if (!deferredTemplate) videoElement.load();
            }

            const posterImg = slide.querySelector('img');
            if (posterImg) {
              posterImg.removeAttribute('srcset');
              if (mediaData.poster) posterImg.src = mediaData.poster;
            }
          } else {
            const imageElement = slide.querySelector('img');
            if (imageElement) {
              imageElement.src = mediaData.src;
              imageElement.alt = mediaData.alt || '';
              imageElement.removeAttribute('srcset');
              const applyDimensions = () => {
                if (imageElement.naturalWidth && imageElement.naturalHeight) {
                  imageElement.setAttribute('width', String(imageElement.naturalWidth));
                  imageElement.setAttribute('height', String(imageElement.naturalHeight));
                }
              };
              if (imageElement.complete) applyDimensions();
              else imageElement.addEventListener('load', applyDimensions, { once: true });
            }
          }

          slide.querySelectorAll('[is="media-lightbox-button"]').forEach((zoomButton) => {
            zoomButton.setAttribute('aria-label', `Open media ${index + 1} in modal`);
          });

          slide.classList.remove('loading');
          slide.querySelectorAll('.media.loading').forEach((el) => el.classList.remove('loading'));
          slidesFragment.appendChild(slide);
        });

        mediaList.appendChild(slidesFragment);

        if (mediaDots && dotTemplate) {
          const dotsFragment = document.createDocumentFragment();
          allMedia.forEach((mediaData, index) => {
            const dot = dotTemplate.cloneNode(true);
            dot.setAttribute('data-media-id', `custom-${index + 1}`);
            dot.setAttribute('data-index', String(index + 1));
            dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
            dot.setAttribute('aria-label', `Go to Slide ${index + 1}${mediaData.alt ? ` - ${mediaData.alt}` : ''}`);
            dot.removeAttribute('hidden');
            dot.classList.remove('loading');

            if (mediaData.type === 'video') {
              dot.classList.add('is-video');

              const icon = document.createElement('span');
              icon.className = 'media-dot__play';

              dot.appendChild(icon);
            }

            const thumb = dot.querySelector('img');
            if (thumb) {
              thumb.src = mediaData.type === 'video' ? (mediaData.poster || mediaData.thumb) : mediaData.src;
              thumb.alt = mediaData.alt || '';
              thumb.removeAttribute('srcset');
            }
            dotsFragment.appendChild(dot);
          });
          mediaDots.appendChild(dotsFragment);
        }

        const waitFrames = (n = 2) =>
          new Promise((resolve) => {
            const step = () => (n-- <= 0 ? resolve() : requestAnimationFrame(step));
            step();
          });

        const waitImages = (timeoutMs = 2000) => {
          const imgs = mediaList.querySelectorAll('img');
          const imagePromises = Array.from(imgs).map(img =>
            new Promise(resolve => {
              if (img.complete) {
                resolve();
                return;
              }
              const handleLoad = () => {
                clearTimeout(timer);
                resolve();
              };
              const timer = setTimeout(() => {
                img.removeEventListener('load', handleLoad);
                resolve();
              }, timeoutMs);
              img.addEventListener('load', handleLoad, { once: true });
            })
          );
          return Promise.all(imagePromises);
        };

        (async () => {          
          await waitFrames(2);
          await waitImages();

          unlockScroll();
          sliderElement.style.overflow = '';
          mediaList.style.overflow = '';

          // reset() measures real layout for its a11y state, so it must run after the overflow lock above is cleared
          sliderElement.reset?.();
          sliderElement.scrollToPosition?.(0, true);
        })();
      }

      getColorGroupIndex(optionGroups) {
        return optionGroups.findIndex((optionGroup) => {
          const slug = optionGroup.dataset.optionSlug || '';
          return slug === 'color' || slug === 'colour' || slug === 'colorway' || slug.includes('color');
        });
      }

      ensureColorSelectionIsAvailable(colorGroup, existingValues, availableValues) {
        if (!colorGroup) return;

        const radios = Array.from(colorGroup.querySelectorAll('input[type="radio"]')).filter((r) => !r.closest('li')?.hidden);

        const firstAvailable = radios.find((r) => availableValues.has(r.value) && !r.classList.contains('disabled'));
        if (firstAvailable) {
          firstAvailable.checked = true;
          firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        const firstExisting = radios.find((r) => existingValues.has(r.value));
        if (firstExisting && !firstExisting.classList.contains('disabled')) {
          firstExisting.checked = true;
          firstExisting.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }

      getOptionGroupBySlug(slug) {
        if (!this.variantSelects) return null;
        return this.variantSelects.querySelector(`[data-option-slug="${slug}"]`);
      }

      getSelectedValueFromGroup(optionGroupElement) {
        if (!optionGroupElement) return null;

        const checkedRadio = optionGroupElement.querySelector('input[type="radio"]:checked');
        if (checkedRadio) return checkedRadio.value;

        return null;
      }

      formatMoney(cents) {
        if (cents == null) return '';

        const value = Math.round(cents / 100);
        const currency = window.Shopify?.currency?.active || 'USD';

        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value);
      }

      updateTierLabelPrices() {
        if (!this.isOptionsPriceEnabled()) return;

        const variantMap = this.getVariantMap();
        if (!variantMap.length) return;

        const COLOR_SLUG = 'colorway';
        const TIER_SLUG = 'tier';

        const colorGroup = this.getOptionGroupBySlug(COLOR_SLUG);
        const tierGroup = this.getOptionGroupBySlug(TIER_SLUG);
        if (!tierGroup) return;

        const selectedColor = this.getSelectedValueFromGroup(colorGroup);
        if (!selectedColor) return;

        const optionGroups = this.getOptionGroups();
        const colorIndex = optionGroups.findIndex((group) => group.getAttribute('data-option-slug') === COLOR_SLUG);
        const tierIndex = optionGroups.findIndex((group) => group.getAttribute('data-option-slug') === TIER_SLUG);

        if (colorIndex < 0 || tierIndex < 0) return;

        const tierLabels = tierGroup.querySelectorAll('.tier-label[data-tier-value]');
        tierLabels.forEach((tierLabel) => {
          const tierValue = tierLabel.getAttribute('data-tier-value');
          const tierPriceElement = tierLabel.querySelector('[data-tier-price]');
          if (!tierValue || !tierPriceElement) return;

          const matchingVariant = variantMap.find((variantEntry) => {
            if (!variantEntry?.available) return false;
            if (!Array.isArray(variantEntry.options)) return false;

            const variantColorValue = variantEntry.options[colorIndex];
            const variantTierValue = variantEntry.options[tierIndex];

            return String(variantColorValue) === String(selectedColor) && String(variantTierValue) === String(tierValue);
          });

          tierPriceElement.textContent = matchingVariant ? this.formatMoney(matchingVariant.price) : '';
        });
      }

      updateRxEquivalent(variantId) {
        const variant = this.getVariantMap().find(v => String(v.id) === String(variantId));
        const rxlinks = document.querySelectorAll('.rx-equivalent:not(.rx-equivalent-buy-buttons)');

        if (!rxlinks.length) return;

        const variantUrl = variant?.rx_product_equivalent_url || '';

        if (variantUrl) {
          rxlinks.forEach((link) => {
            link.setAttribute('href', variantUrl);
            link.classList.remove('hidden');
            link.parentElement?.classList.add('with-rx');
          });
        } else {
          rxlinks.forEach((link) => {
            link.removeAttribute('href');
            link.classList.add('hidden');
            link.parentElement?.classList.remove('with-rx');
          });
        }
      }

      isOptionsPriceEnabled() {
        const raw = this.getAttribute('data-enable-options-price');
        return raw === 'true' || raw === true;
      }

      getTierGroup() {
        return this.getOptionGroupBySlug('tier');
      }

      getSelectedTierValue() {
        const tierGroup = this.getTierGroup();
        return this.getSelectedValueFromGroup(tierGroup);
      }

      async fetchAndSwapDynamicWrapperByVariantId(variantId, view) {
        if (!variantId) return;
        if (!view) return;

        if (!this.dynamicViewCache) this.dynamicViewCache = new Map();

        const wrapper = document.getElementById('dynamic-section-wrapper');
        const content = document.getElementById('dynamic-section-content');
        if (!wrapper || !content) return;

        const cacheKey = `${view}::${variantId}`;

        wrapper.setAttribute('aria-busy', 'true');
        wrapper.classList.add('is-loading');

        // Serve from cache if available
        const cachedTpl = this.dynamicViewCache.get(cacheKey);
        if (cachedTpl) {
          requestAnimationFrame(() => {
            content.replaceChildren(cachedTpl.content.cloneNode(true));
            wrapper.removeAttribute('aria-busy');
            wrapper.classList.remove('is-loading');
          });
          return;
        }

        // Abort any in-flight swap
        if (this.tierSwapAbort) this.tierSwapAbort.abort();
        this.tierSwapAbort = new AbortController();

        const url = `${window.location.pathname}?view=${encodeURIComponent(view)}&variant=${encodeURIComponent(variantId)}`;

        try {
          const res = await fetch(url, { signal: this.tierSwapAbort.signal });
          if (!res.ok) return;

          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');

          const nextMain = doc.querySelector('main');
          if (!nextMain) return;

          const tpl = document.createElement('template');
          tpl.innerHTML = Array.from(nextMain.children).map((el) => el.outerHTML).join('');
          this.dynamicViewCache.set(cacheKey, tpl);

          requestAnimationFrame(() => {
            content.replaceChildren(tpl.content.cloneNode(true));
            wrapper.removeAttribute('aria-busy');
            wrapper.classList.remove('is-loading');
          });

          document.dispatchEvent(new CustomEvent('variant:tier-swap:done', { detail: { variantId, view } }));
        } catch (err) {
          if (err?.name === 'AbortError') return;
          console.error(err);
        } finally {
          // If fetch fails before rAF swap, remove busy state
          // (rAF swap also removes it)
          if (wrapper.classList.contains('is-loading')) {
            wrapper.removeAttribute('aria-busy');
            wrapper.classList.remove('is-loading');
          }
        }
      }

      getSelectedTierRadio() {
        const tierGroup = this.getTierGroup();
        if (!tierGroup) return null;
        return tierGroup.querySelector('input[type="radio"]:checked');
      }

      getSelectedTierViewName() {
        const tierRadio = this.getSelectedTierRadio();
        if (!tierRadio) return null;

        // Use whatever attribute you set in Liquid:
        // e.g. data-dynamic-template="tier_knockterra_plus"
        return tierRadio.getAttribute('data-dynamic-template') || null;
      }

      updateFsTierContent(){
        let tierValue = this.getSelectedTierValue().toLowerCase();
        if (tierValue.includes('+')){
          tierValue = tierValue.replaceAll('+','plus');
        }

        const fsTierElements = document.querySelectorAll('[data-tier-handle]');
        if (!fsTierElements.length) return;

        [...fsTierElements].forEach(ele=>{
          const isActive = tierValue === ele.getAttribute('data-tier-handle');
          ele.toggleAttribute('hidden', !isActive);

          if (!isActive) return;

          const slideshowEle = ele.querySelector('slideshow-hero');
          slideshowEle?.init?.();
          slideshowEle?.slider?.resize?.();
        })
      }

      async updateSpecListSections(variantId) {
        if (!variantId) return;
        const containers = document.querySelectorAll('[data-spec-source="variant_metafield"][data-section-id]');
        if (!containers.length) return;

        const sectionIds = [...new Set([...containers].map((el) => el.dataset.sectionId))];

        await Promise.all(
          sectionIds.map(async (sectionId) => {
            try {
              const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}&variant=${encodeURIComponent(variantId)}`;
              const res = await fetch(url);
              if (!res.ok) return;

              const html = await res.text();
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const next = doc.querySelector('[data-spec-source="variant_metafield"]');
              if (!next) return;

              const current = document.querySelector(`[data-spec-source="variant_metafield"][data-section-id="${sectionId}"]`);
              if (!current) return;

              requestAnimationFrame(() => {
                current.replaceChildren(...Array.from(next.childNodes));
                current.setAttribute('data-variant-id', next.getAttribute('data-variant-id'))
                current.setAttribute('data-using-mo-fallback', next.getAttribute('data-using-mo-fallback'))
              });
            } catch (err) {
              if (err?.name !== 'AbortError') console.error(err);
            }
          })
        );
      }
    }
  );
}
